import { createContext, useContext, useEffect, useState } from "react";
import { apiFetch, getErrorMessage, saveToken, clearToken } from "../utils/api";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const refreshUser = async () => {
        try {
            const me = await apiFetch("/api/auth/me");
            setUser(me);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = async (data) => {
        try {
            const res = await apiFetch("/api/auth/login", {
                method: "POST",
                body: JSON.stringify(data),
                headers: { "Content-Type": "application/json" }
            });
            // Save token from response body so it works cross-domain
            if (res?.accessToken) saveToken(res.accessToken);
            await refreshUser();
        } catch (error) {
            throw new Error(getErrorMessage(error));
        }
    };

    const signup = async (data) => {
        try {
            const res = await apiFetch("/api/auth/register", {
                method: "POST",
                body: JSON.stringify(data),
                headers: { "Content-Type": "application/json" }
            });
            // Save token from response body so it works cross-domain
            if (res?.accessToken) saveToken(res.accessToken);
            await refreshUser();
        } catch (error) {
            throw new Error(getErrorMessage(error));
        }
    };

    const logout = async () => {
        try {
            await apiFetch("/api/auth/logout", { method: "POST" });
        } catch (error) {
            console.error("Logout failed", error);
        }
        clearToken();
        setUser(null);
        navigate("/login");
        toast.success("Logged out successfully!");
    };

    useEffect(() => {
        refreshUser();
    }, []);

    return (
        <AuthContext.Provider value={{ user, isLoggedIn: !!user, loading, refreshUser, login, signup, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};